import { getSends, sendEnquiry } from './enquiriesApi';
import { acceptResult, attemptKey, loadAttempt, saveAttempt, rejectResult, mainStageKeys } from './enquiryAttempt';
import { registerSessionReset, sessionIsCurrent, sessionTicket } from '../../../utils/sessionLifecycle';

export const ENQUIRY_BACKGROUND_EVENT = 'srm:enquiry-background';
const flights = new Map(), watches = new Map(), snapshots = new Map(), notices = new Map();
const socketActivity = new Map();
export function claimEnquiryNotice(user, query, id, signature) {
  const key = `${user}:${query}:${id}`;
  if (notices.get(key) === signature) return false;
  notices.set(key, signature); return true;
}
const running = attempt => attempt && !attempt.storageBlocked && ['processing', 'unknown'].includes(attempt.status);
const supplierCount = attempt => mainStageKeys(attempt).length;
function publish(user, query, value, ticket) {
  if (!sessionIsCurrent(ticket)) return;
  const detail = { userId: String(user), task_id: String(query), ...value };
  snapshots.set(attemptKey(user, query), detail);
  window.dispatchEvent(new CustomEvent(ENQUIRY_BACKGROUND_EVENT, { detail }));
}
export function enquirySnapshots(user) {
  // Recover this browser's actor partition, never discover sends from board rows.
  const prefix = `uzdpart360:enquiry:${user}:`;
  try {
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key?.startsWith(prefix) || snapshots.has(key)) continue;
      const query = key.slice(prefix.length), saved = loadAttempt(user, query);
      if (running(saved)) snapshots.set(key, { userId: String(user), task_id: query, type: 'progress', ...fromAttempt(saved) });
    }
  } catch { /* Unreadable storage cannot authorize recovery. */ }
  return [...snapshots.values()].filter(row => row.userId === String(user));
}
export const enquiryInFlight = (user, query) => flights.has(attemptKey(user, query));
export function observeEnquiryProgress(user, event) {
  if (user == null || !event?.outreach_attempt_id || event.task_id == null) return;
  const saved = loadAttempt(user, event.task_id);
  if (!running(saved) || saved.result?.outreach_attempt?.id !== event.outreach_attempt_id) return;
  socketActivity.set(attemptKey(user, event.task_id), Date.now());
  snapshots.set(attemptKey(user, event.task_id), { ...event, userId: String(user), task_id: String(event.task_id), type: 'progress' });
  watchEnquiry(user, event.task_id);
}
function stop(scope) { const watch = watches.get(scope); if (watch) clearTimeout(watch.timer); watches.delete(scope); }
function fromAttempt(attempt) {
  const rows = Object.entries(attempt.result?.outreach_attempt?.stage_results || {}).filter(([key]) => mainStageKeys(attempt).includes(key));
  const processed = rows.filter(([, row]) => ['submitted', 'failed', 'rejected', 'not_sent'].includes(row.status)).length;
  const failed = new Set();
  for (const [key, stage] of Object.entries(attempt.result?.outreach_attempt?.stage_results || {})) {
    if (!['failed', 'rejected', 'not_sent'].includes(stage.status)) continue;
    const match = /^(supplier|team):(\d+):/.exec(key);
    const id = match ? `${match[1]}:${match[2]}` : null;
    if (id) failed.add(String(id));
  }
  return { outreach_attempt_id: attempt.result?.outreach_attempt?.id, total: supplierCount(attempt), processed,
    failed_count: failed.size };
}
export function executeEnquiryAttempt(saved, onDispatched, onFailure, { clearMissing = false } = {}) {
  const { userId: user, queryPk: query } = saved, scope = attemptKey(user, query);
  if (flights.has(scope)) return flights.get(scope);
  const ticket = sessionTicket();
  // Saving is synchronous and must succeed before the UI closes or HTTP starts.
  saveAttempt(saved);
  let release;
  const flight = new Promise(resolve => { release = resolve; });
  flights.set(scope, flight);
  publish(user, query, { type: 'progress', ...fromAttempt(saved) }, ticket);
  let request;
  try { request = sendEnquiry(Number(query), saved.payload, saved.idempotencyKey); }
  catch (error) { request = Promise.reject(error); }
  watchEnquiry(user, query, ticket);
  onDispatched?.();
  let receipt;
  Promise.resolve(request).then(response => { receipt = response; return acceptResult(saved, response); }, error => {
    if (clearMissing && (error?.response?.status === 404 || (error?.response?.data?.code || error?.code) === 'MESSAGING_OUTREACH_ATTEMPT_NOT_FOUND')) return null;
    onFailure?.(error); return rejectResult(saved, error);
  }).then(next => {
    try {
      if (next === null) {
        // Only the server's missing receipt releases a recoverable local fence.
        if (loadAttempt(user, query)?.idempotencyKey === saved.idempotencyKey) localStorage.removeItem(scope);
        stop(scope); snapshots.delete(scope);
        return null;
      }
      saveAttempt(next);
      if (next.status === 'completed') {
        stop(scope);
        publish(user, query, { type: 'finished', status: next.status, task: receipt?.data, ...fromAttempt(next) }, ticket);
      } else if (running(next) && sessionIsCurrent(ticket)) watchEnquiry(user, query, ticket);
      else {
        stop(scope);
        publish(user, query, { type: 'finished', status: next.status, error: next.error, ...fromAttempt(next) }, ticket);
      }
    } catch {
      stop(scope);
      next = { ...saved, status: 'blocked', storageBlocked: true, error: 'Cannot persist this send safely. Do not resend until its outcome is verified.' };
      publish(user, query, { type: 'finished', status: 'blocked', error: next.error, ...fromAttempt(saved) }, ticket);
    }
    return next;
  }).then(next => { flights.delete(scope); if (sessionIsCurrent(ticket)) window.dispatchEvent(new Event('enquiry-attempt-updated')); release(next); }, () => {
    flights.delete(scope); release({ ...saved, status: 'blocked', storageBlocked: true });
  });
  return flight;
}

function historyProgress(row) {
  const recipients = row.recipients || [];
  const terminal = recipient => recipient.parts?.length && recipient.parts.every(part => ['sent', 'submitted', 'failed', 'rejected', 'not_sent'].includes(part.stage_status));
  return { outreach_attempt_id: row.outreach_attempt_id, queryId: row.query_id, total: recipients.length,
    processed: row.status === 'completed' ? recipients.length : recipients.filter(terminal).length,
    failed_count: recipients.filter(recipient => recipient.parts?.some(part => ['failed', 'rejected', 'not_sent'].includes(part.stage_status))).length };
}
export async function refreshEnquiryHistory(user, query, { baseline = false, ticket = sessionTicket() } = {}) {
  const saved = loadAttempt(user, query);
  if (!running(saved) || !sessionIsCurrent(ticket)) return;
  const history = await getSends(Number(query));
  if (!sessionIsCurrent(ticket) || String(history?.query_pk ?? query) !== String(query) || !Array.isArray(history?.sends)) return;
  const id = saved?.result?.outreach_attempt?.id;
  const previous = snapshots.get(attemptKey(user, query));
  const expected = (running(saved) ? id : null) || (previous?.type === 'progress' ? previous.outreach_attempt_id : null);
  const row = expected ? history.sends.find(item => item.outreach_attempt_id === expected) : null;
  if (!row || String(row.query_pk ?? query) !== String(query)) return;
  if (row.status === 'processing') {
    publish(user, query, { type: 'progress', ...historyProgress(row) }, ticket);
    watchEnquiry(user, query, ticket);
  } else if (!baseline || previous?.type === 'progress') {
    stop(attemptKey(user, query));
    publish(user, query, { type: 'finished', status: row.status, ...historyProgress(row) }, ticket);
    // History is progress evidence, not a replacement for validated command
    // receipts. Retrieve the final receipt by replaying the identical key/body.
    if (running(saved) && id === row.outreach_attempt_id && !enquiryInFlight(user, query)) executeEnquiryAttempt(saved);
  }
}
export function watchEnquiry(user, query, ticket = sessionTicket()) {
  const scope = attemptKey(user, query);
  if (watches.has(scope) || !sessionIsCurrent(ticket)) return;
  if (!running(loadAttempt(user, query))) return;
  const watch = { startedAt: Date.now() };
  watches.set(scope, watch);
  const tick = async () => {
    if (!sessionIsCurrent(ticket) || watches.get(scope) !== watch) { stop(scope); return; }
    if (!running(loadAttempt(user, query))) { stop(scope); return; }
    if (Date.now() - (socketActivity.get(scope) ?? watch.startedAt) <= 10000) {
      watch.timer = setTimeout(tick, 5000); return;
    }
    try { await refreshEnquiryHistory(user, query, { ticket }); } catch { /* A read failure never authorizes a new send. */ }
    const saved = loadAttempt(user, query);
    if (running(saved) && !saved.result && !enquiryInFlight(user, query) && sessionIsCurrent(ticket)) {
      // Timeout/reload recovery is the existing frozen command, never a new key.
      try { executeEnquiryAttempt(saved); } catch { stop(scope); }
    }
    if (watches.get(scope) === watch) watch.timer = setTimeout(tick, 5000);
  };
  watch.timer = setTimeout(tick, 5000);
}
export function recoverEnquiry(user, query) {
  if (user == null || query == null) return;
  const saved = loadAttempt(user, query);
  if (!running(saved)) return;
  if (!saved.result && !enquiryInFlight(user, query)) executeEnquiryAttempt(saved);
  watchEnquiry(user, query);
}
registerSessionReset(() => {
  [...watches.keys()].forEach(stop); snapshots.clear(); notices.clear(); socketActivity.clear();
  // In-flight HTTP may record its original actor's receipt, but old-session
  // tickets prevent notifications, polling or another HTTP request afterward.
});
